import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import axios from '../../../../config/axios'
import { PipelineProvider, usePipelineContext } from '../PipelineContext'
import Kanban from '../kanban'
import { PipelinePageShell, PipelineMain } from '../style'
import { ViewModeSelector, SortBySelector, FilterDropdown, ControlsContainer, OutlineButton } from '../PipelineControls'
import { SectionName, LeftSide, SubHeaderContainer, RightSide, ButtonCreate } from '../../../../components/styles/subheader-style'
import { ModulePipeline } from '../types'
import { useOutletContext } from 'react-router-dom'
import { TypeMainOutlet } from '../..'
import { RefreshCw } from 'lucide-react'
import { Ellipsis, Plus, LayoutGrid, BarChart3, Zap, FileText, ArrowLeft, Building2, Wrench } from 'lucide-react'
import PipelineOptionsDropdown from '../PipelineOptionsDropdown'
import AutomationsPage from '../Automations'
import CreateAutomation from '../Automations/CreateAutomation'
import { TabsDiv, Tab, OptionsButton } from '../../../../components/styles/subheader-style'

interface ModuleKanbanProps {
  moduleType: 'production' | 'sales' | 'product_development'
  moduleName: string
  subModule?: 'steel' | 'fashion'
  headerLeftContent?: React.ReactNode
}


const BackButton = styled.div`
  font-weight: bold;
  user-select: none;
  display: flex;
  height: 100%;
  align-items: center;
  font-size: .8rem;
  padding: 0.5rem 1.2rem;
  color: #0090D3;
  cursor: pointer;
  background-color: transparent;
  border: 2px solid #0090D3;
  border-radius: 2rem;
  position: relative;
  z-index: 10;
  pointer-events: auto;
  transition: all 0.2s ease;
  gap: 0.5rem;

  &:hover {
    background-color: #E6F4FB;
  }
`

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SyncButton = styled(OutlineButton)<{ $syncing?: boolean }>`
  transition: background-color 0.2s ease, opacity 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    animation: ${({ $syncing }) => ($syncing ? 'spin 1s linear infinite' : 'none')};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`

const ModuleKanbanInner: React.FC<ModuleKanbanProps> = ({ moduleType, moduleName, subModule, headerLeftContent }) => {
  const { 
    viewMode, setViewMode, sortBy, setSortBy, setIsAddingTableCard, setContextPipelineId,
    setIsConfiguringPipeline, setViewOrEditTagsModalIsOpen, setAutomationsPageIsOpen,
    isConfiguringPipeline, automationsPageIsOpen,
    activeAutomationTab, setActiveAutomationTab,
    isCreatingAutomation, setIsCreatingAutomation,
    showArchived
  } = usePipelineContext()
  const [viewDropdownOptions, setViewDropdownOptions] = useState<boolean>(false)
  const mainOutletContext = useOutletContext<TypeMainOutlet>()
  const [pipeline, setPipeline] = useState<ModulePipeline | null>(null)
  const [loading, setLoading] = useState(true)
  const [settingUp, setSettingUp] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<{ created: number; skipped: number; total: number; moved?: number } | null>(null)
  const [pendingViewSync, setPendingViewSync] = useState<string | null>(null)
  const syncingRef = useRef(false)
  const syncScopeRef = useRef('')
  const setupCreatedPipelineRef = useRef(false)
  const skipNextAutoSyncRef = useRef(false)

  const isProductionModule = moduleType === 'production'

  const resolveSubModule = (mode: string) => {
    if (isProductionModule && (mode === 'sector' || mode === 'service')) {
      return `${subModule || 'steel'}:${mode}`
    }
    return subModule
  }

  const resolvedSubModule = resolveSubModule(viewMode)

  const buildSyncScope = (overrideViewType?: string) => {
    const scopeView = overrideViewType || viewMode
    const scopeSubModule = resolveSubModule(scopeView) || 'default'
    const pipelineScope = pipeline?.id ?? 'none'
    return `${moduleType}:${scopeSubModule}:${pipelineScope}`
  }

  const lastPipelineIdRef = useRef<number | null>(null)

  const productionViewOptions = [
    { value: 'sector', label: 'Setor', icon: <Building2 size={16} /> },
    { value: 'service', label: 'Serviços', icon: <Wrench size={16} /> },
  ]

  const loadPipeline = async () => {
    try {
      const params = new URLSearchParams({ moduleType })
      if (resolvedSubModule) params.set('subModule', resolvedSubModule)
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline?${params.toString()}`)
      const pipelines = response.data?.data || []
      if (pipelines.length > 0) {
        setPipeline(pipelines[0])
        setContextPipelineId(pipelines[0].id)
        return pipelines[0]
      }
      return null
    } catch (error) {
      console.error('[ModuleKanban] Erro ao buscar pipeline do módulo:', error)
      return null
    }
  }

  const isInitializingRef = useRef(false)

  useEffect(() => {
    isInitializingRef.current = false
    
    const init = async () => {
      if (isInitializingRef.current) return
      isInitializingRef.current = true

      const existing = await loadPipeline()
      if (!existing) {
        setSettingUp(true)
        try {
          const setupResponse = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/setup-module`, {
            moduleType,
            ...(resolvedSubModule ? { subModule: resolvedSubModule } : {}),
          })
          const setupData = setupResponse.data?.data
          if (setupData) {
            setupCreatedPipelineRef.current = true
            setPipeline(setupData)
            setContextPipelineId(setupData.id)
          }
        } catch (setupError: any) {
          const message = setupError?.response?.data?.message || setupError?.message || 'Erro desconhecido'
          console.error('[ModuleKanban] Erro no auto-setup:', message)
        } finally {
          setSettingUp(false)
        }
      }
      setLoading(false)
    }
    init()
    
    return () => {
      isInitializingRef.current = false
    }
  }, [moduleType, resolvedSubModule, setContextPipelineId])

  const handleSync = async (overrideViewType?: string) => {
    if (!pipeline || syncingRef.current) return
    const syncScope = buildSyncScope(overrideViewType)
    console.debug('[ModuleKanban] sync:start', {
      syncScope,
      overrideViewType: overrideViewType || null,
      viewMode,
      resolvedSubModule,
      pipelineId: pipeline.id,
    })
    syncScopeRef.current = syncScope
    syncingRef.current = true
    setSyncing(true)
    setSyncResult(null)
    try {
      const currentViewType = overrideViewType || viewMode
      const syncPayload: any = {
        moduleType,
        pipelineId: pipeline.id,
        ...(resolvedSubModule ? { subModule: resolvedSubModule } : {}),
      }
      if (isProductionModule && (currentViewType === 'sector' || currentViewType === 'service')) {
        syncPayload.viewType = currentViewType
      }
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/sync-module`, syncPayload)
      if (syncScopeRef.current === syncScope) {
        setSyncResult(response.data?.data)
      }
      console.debug('[ModuleKanban] sync:done', {
        syncScope,
        viewMode,
        resolvedSubModule,
        pipelineId: pipeline.id,
        result: response.data?.data,
        accepted: syncScopeRef.current === syncScope,
      })
      window.dispatchEvent(new Event('pipeline-sync-complete'))
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Erro desconhecido'
      console.debug('[ModuleKanban] sync:error', {
        syncScope,
        viewMode,
        resolvedSubModule,
        pipelineId: pipeline.id,
        message,
      })
      alert(`Erro ao sincronizar: ${message}`)
    } finally {
      if (syncScopeRef.current === syncScope) {
        syncingRef.current = false
        setSyncing(false)
      }
      console.debug('[ModuleKanban] sync:finally', {
        syncScope,
        viewMode,
        resolvedSubModule,
        pipelineId: pipeline.id,
        syncingState: syncingRef.current,
        accepted: syncScopeRef.current === syncScope,
      })
    }
  }

  const handleViewModeChange = (newMode: string) => {
    console.debug('[ModuleKanban] view:change', {
      from: viewMode,
      to: newMode,
      pipelineId: pipeline?.id ?? null,
    })
    if (newMode !== viewMode) {
      skipNextAutoSyncRef.current = true
    }
    setViewMode(newMode as any)
  }

  useEffect(() => {
    const nextScope = buildSyncScope()
    syncScopeRef.current = nextScope
    syncingRef.current = false
    setSyncing(false)
    setSyncResult(null)
    console.debug('[ModuleKanban] sync:reset', {
      nextScope,
      viewMode,
      resolvedSubModule,
      pipelineId: pipeline?.id ?? null,
    })
  }, [resolvedSubModule, pipeline?.id])

  useEffect(() => {
    // Intentionally allow configuring pipelines for sales modules.
  }, [moduleType, isConfiguringPipeline, setIsConfiguringPipeline])

  useEffect(() => {
    if (pipeline && isProductionModule && (viewMode === 'sector' || viewMode === 'service')) {
      if (lastPipelineIdRef.current !== null && lastPipelineIdRef.current !== pipeline.id) {
        if (setupCreatedPipelineRef.current) {
          setupCreatedPipelineRef.current = false
          skipNextAutoSyncRef.current = false
          handleSync()
        } else if (skipNextAutoSyncRef.current) {
          skipNextAutoSyncRef.current = false
          console.debug('[ModuleKanban] sync:skip', {
            reason: 'view-change',
            viewMode,
            resolvedSubModule,
            pipelineId: pipeline.id,
          })
        } else {
          handleSync()
        }
      }
      lastPipelineIdRef.current = pipeline.id
    }
  }, [pipeline, viewMode])

  useEffect(() => {
    const onRequestSync = () => { handleSync() }
    window.addEventListener('pipeline-request-sync', onRequestSync)
    return () => window.removeEventListener('pipeline-request-sync', onRequestSync)
  }, [pipeline, syncing, viewMode])

  if (loading || settingUp) {
    return <div style={{ padding: '2rem', color: '#666' }}>{settingUp ? 'Configurando pipeline...' : 'Carregando...'}</div>
  }

  if (!pipeline) {
    return (
      <div style={{ padding: '2rem', color: '#666', textAlign: 'center' }}>
        Nenhuma pipeline configurada para o módulo {moduleName}.
      </div>
    )
  }

  
  if (isCreatingAutomation) {
    return (
      <PipelinePageShell>
        <PipelineMain>
          <CreateAutomation />
        </PipelineMain>
      </PipelinePageShell>
    )
  }

  if (automationsPageIsOpen) {
    return (
      <PipelinePageShell>
        <SubHeaderContainer>
          <LeftSide>
            <SectionName>Automação</SectionName>
            <TabsDiv>
              <Tab
                $isFirst
                $isActive={activeAutomationTab === 'automations'}
                onClick={() => setActiveAutomationTab('automations')}
              >
                <LayoutGrid size={16} style={{ marginRight: '0.4rem' }} /> Automações
              </Tab>
              <Tab
                $isActive={activeAutomationTab === 'connections'}
                onClick={() => setActiveAutomationTab('connections')}
              >
                <BarChart3 size={16} style={{ marginRight: '0.4rem' }} /> Conexões
              </Tab>
              <Tab
                $isActive={activeAutomationTab === 'integration'}
                onClick={() => setActiveAutomationTab('integration')}
              >
                <Zap size={16} style={{ marginRight: '0.4rem' }} /> Integração
              </Tab>
              <Tab
                $isLast
                $isActive={activeAutomationTab === 'documentation'}
                onClick={() => setActiveAutomationTab('documentation')}
              >
                <FileText size={16} style={{ marginRight: '0.4rem' }} /> Documentação
              </Tab>
            </TabsDiv>
          </LeftSide>
          <RightSide>
            <BackButton onClick={() => setAutomationsPageIsOpen(false)}>
              <ArrowLeft size={16} /> Voltar
            </BackButton>
            <ButtonCreate onClick={() => setIsCreatingAutomation(true)}>
              <Plus size={16} style={{ marginRight: '0.4rem' }} /> Nova automação
            </ButtonCreate>
          </RightSide>
        </SubHeaderContainer>
        <PipelineMain>
          <AutomationsPage />
        </PipelineMain>
      </PipelinePageShell>
    )
  }

  return (
    <PipelinePageShell>
      <SubHeaderContainer>
        {headerLeftContent ? (
          <LeftSide>{headerLeftContent}</LeftSide>
        ) : (
          <LeftSide>
            <SectionName>
              {pipeline.name || moduleName}{showArchived && <span style={{ color: '#dc2626', marginLeft: '0.4rem' }}>(Arquivados)</span>}
            </SectionName>
            <ViewModeSelector
              value={viewMode}
              onChange={handleViewModeChange}
              {...(isProductionModule ? { options: productionViewOptions } : { boardLabel: 'Quadro', tableLabel: 'Tabela' })}
            />
          </LeftSide>
        )}
        <RightSide>
          {headerLeftContent && (
            <ViewModeSelector
              value={viewMode}
              onChange={handleViewModeChange}
              {...(isProductionModule ? { options: productionViewOptions } : { boardLabel: 'Quadro', tableLabel: 'Tabela' })}
            />
          )}
          <ControlsContainer>
            <SortBySelector value={sortBy} onChange={(v: string) => setSortBy(v as any)} />
            <FilterDropdown />
            <SyncButton onClick={() => handleSync()} disabled={syncing} $syncing={syncing}>
              <RefreshCw size={16} />
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </SyncButton>
          </ControlsContainer>
          {syncResult && (
            <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>
              {syncResult.created > 0
                ? `${syncResult.created} criado${syncResult.created > 1 ? 's' : ''}`
                : 'Todos já sincronizados'}
            </span>
          )}
          {viewMode === 'table' && !isProductionModule && (
            <ButtonCreate onClick={() => setIsAddingTableCard(true)}>
              + Adicionar card
            </ButtonCreate>
          )}
          {isConfiguringPipeline &&
            <ButtonCreate onClick={() => setIsConfiguringPipeline(false)}>Concluído</ButtonCreate>
          }
          <OptionsButton onClick={() => setViewDropdownOptions(!viewDropdownOptions)}>
            <Ellipsis size={18} />
          </OptionsButton>
          {viewDropdownOptions && pipeline && (
            <PipelineOptionsDropdown
              onClose={() => setViewDropdownOptions(false)}
              onConfigurePipeline={() => setIsConfiguringPipeline(true)}
              onViewTags={() => setViewOrEditTagsModalIsOpen(true)}
              onViewAutomations={() => setAutomationsPageIsOpen(true)}
              pipelineId={pipeline.id}
              pipelineName={pipeline.name || moduleName}
              canEditPipeline={true}
            />
          )}
        </RightSide>
      </SubHeaderContainer>
      <PipelineMain>
        <Kanban />
      </PipelineMain>
    </PipelinePageShell>
  )
}

const ModuleKanban: React.FC<ModuleKanbanProps> = (props) => {
  return (
    <PipelineProvider moduleType={props.moduleType} subModule={props.subModule}>
      <ModuleKanbanInner {...props} />
    </PipelineProvider>
  )
}

export default ModuleKanban
